import { useCallback, useEffect, useRef, useState } from 'react';
import { apiAdminGetIngestionStatus, getWebSocketUrl } from '../data/api';
import {
  isIngestionJobStatus,
  isTerminalIngestionStatus,
  type IngestionJobStatus,
} from '../data/ingestion';

export const ACTIVE_INGESTION_JOB_STORAGE_KEY = 'active-ingestion-job-id';

const INITIAL_RECONNECT_DELAY_MS = 500;
const MAX_RECONNECT_DELAY_MS = 15_000;
const SNAPSHOT_POLL_INTERVAL_MS = 3_000;

type SocketMessage =
  | { type: 'snapshot' | 'status'; job: IngestionJobStatus }
  | { type: 'error'; code: string; message: string };

export type IngestionConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'closed';

interface UseIngestionStatusOptions {
  getAccessToken: () => Promise<string>;
}

export interface UseIngestionStatusResult {
  activeJobId: string | null;
  job: IngestionJobStatus | null;
  connectionState: IngestionConnectionState;
  error: string;
  trackJob: (jobId: string) => void;
  reset: () => void;
}

function readStoredJobId(): string | null {
  try {
    const storedJobId = window.sessionStorage.getItem(ACTIVE_INGESTION_JOB_STORAGE_KEY);
    return storedJobId?.trim() || null;
  } catch {
    return null;
  }
}

function removeStoredJobId(): void {
  try {
    window.sessionStorage.removeItem(ACTIVE_INGESTION_JOB_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }
}

function getSafeFailureMessage(errorMessage: string | null): string {
  const message = errorMessage?.trim();
  if (!message || /bearer|authorization|access.?token|x-amz-|signature/i.test(message)) {
    return 'The ingestion failed. Review the files and try again.';
  }

  return `The ingestion failed: ${message.slice(0, 240)}`;
}

function getSocketErrorMessage(message: string): string {
  const normalized = message.trim();
  if (!normalized || /bearer|authorization|access.?token|x-amz-|signature/i.test(normalized)) {
    return 'Live ingestion updates are unavailable. Retrying…';
  }

  return `Live updates unavailable: ${normalized.slice(0, 180)}`;
}

function parseSocketMessage(value: unknown): SocketMessage | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const message = value as Record<string, unknown>;
  if (
    (message.type === 'snapshot' || message.type === 'status') &&
    isIngestionJobStatus(message.job)
  ) {
    return { type: message.type, job: message.job };
  }

  if (
    message.type === 'error' &&
    typeof message.code === 'string' &&
    typeof message.message === 'string'
  ) {
    return { type: 'error', code: message.code, message: message.message };
  }

  return null;
}

function getReconnectDelay(attempt: number): number {
  const exponentialDelay = Math.min(
    MAX_RECONNECT_DELAY_MS,
    INITIAL_RECONNECT_DELAY_MS * 2 ** attempt,
  );
  const jitter = 0.75 + Math.random() * 0.5;
  return Math.round(exponentialDelay * jitter);
}

export function useIngestionStatus({
  getAccessToken,
}: UseIngestionStatusOptions): UseIngestionStatusResult {
  const [activeJobId, setActiveJobId] = useState<string | null>(readStoredJobId);
  const [job, setJob] = useState<IngestionJobStatus | null>(null);
  const [connectionState, setConnectionState] = useState<IngestionConnectionState>(
    activeJobId ? 'connecting' : 'idle',
  );
  const [error, setError] = useState('');
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const terminalRef = useRef(false);

  const closeSocket = useCallback(() => {
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket) {
      socket.onclose = null;
      socket.onerror = null;
      socket.onmessage = null;
      socket.onopen = null;
      socket.close();
    }
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const trackJob = useCallback(
    (jobId: string) => {
      clearReconnectTimer();
      closeSocket();
      terminalRef.current = false;
      reconnectAttemptRef.current = 0;
      setError('');
      setJob(null);
      setConnectionState('connecting');
      setActiveJobId(jobId);
    },
    [clearReconnectTimer, closeSocket],
  );

  const reset = useCallback(() => {
    clearReconnectTimer();
    closeSocket();
    terminalRef.current = false;
    reconnectAttemptRef.current = 0;
    removeStoredJobId();
    setActiveJobId(null);
    setJob(null);
    setError('');
    setConnectionState('idle');
  }, [clearReconnectTimer, closeSocket]);

  useEffect(() => {
    try {
      if (activeJobId) {
        window.sessionStorage.setItem(ACTIVE_INGESTION_JOB_STORAGE_KEY, activeJobId);
      } else {
        removeStoredJobId();
      }
    } catch {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  }, [activeJobId]);

  useEffect(() => {
    if (!activeJobId) {
      return;
    }

    let cancelled = false;
    let snapshotPollTimer: number | null = null;
    let snapshotRequestInFlight = false;
    const jobId = activeJobId;

    const stopSnapshotPolling = () => {
      if (snapshotPollTimer !== null) {
        window.clearInterval(snapshotPollTimer);
        snapshotPollTimer = null;
      }
    };

    const applySnapshot = (nextJob: IngestionJobStatus) => {
      if (cancelled || nextJob.id !== jobId || !isIngestionJobStatus(nextJob)) {
        return;
      }

      setJob(nextJob);
      setError(nextJob.status === 'FAILED' ? getSafeFailureMessage(nextJob.errorMessage) : '');

      if (isTerminalIngestionStatus(nextJob.status)) {
        terminalRef.current = true;
        stopSnapshotPolling();
        clearReconnectTimer();
        closeSocket();
        removeStoredJobId();
        setConnectionState('closed');
      }
    };

    const fetchSnapshot = async () => {
      if (snapshotRequestInFlight || cancelled || terminalRef.current) {
        return;
      }

      snapshotRequestInFlight = true;
      try {
        const accessToken = await getAccessToken();
        if (cancelled) {
          return;
        }

        const snapshot = await apiAdminGetIngestionStatus(jobId, accessToken);
        applySnapshot(snapshot);
      } catch {
        if (!cancelled) {
          setError('Unable to load the latest ingestion status. Live updates will retry.');
        }
      } finally {
        snapshotRequestInFlight = false;
      }
    };

    const scheduleReconnect = () => {
      if (cancelled || terminalRef.current || reconnectTimerRef.current !== null) {
        return;
      }

      const attempt = reconnectAttemptRef.current;
      reconnectAttemptRef.current = Math.min(attempt + 1, 10);
      setConnectionState('reconnecting');
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        void fetchSnapshot().finally(() => {
          if (!cancelled && !terminalRef.current) {
            void connect();
          }
        });
      }, getReconnectDelay(attempt));
    };

    const connect = async () => {
      if (cancelled || terminalRef.current) {
        return;
      }

      setConnectionState(reconnectAttemptRef.current > 0 ? 'reconnecting' : 'connecting');

      let accessToken: string;
      try {
        accessToken = await getAccessToken();
      } catch {
        if (!cancelled) {
          setError('Unable to authenticate live ingestion updates. Retrying…');
          scheduleReconnect();
        }
        return;
      }

      if (cancelled || terminalRef.current) {
        return;
      }

      if (typeof WebSocket === 'undefined') {
        setError('Live ingestion updates are unavailable. Retrying…');
        scheduleReconnect();
        return;
      }

      const socket = new WebSocket(getWebSocketUrl());
      socketRef.current = socket;

      socket.onopen = () => {
        if (cancelled || terminalRef.current) {
          socket.close();
          return;
        }

        reconnectAttemptRef.current = 0;
        setConnectionState('connected');
        socket.send(JSON.stringify({ type: 'subscribe', accessToken, jobId }));
      };

      socket.onmessage = (event) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(String(event.data));
        } catch {
          return;
        }

        const message = parseSocketMessage(parsed);
        if (!message) {
          return;
        }

        if (message.type === 'error') {
          setError(getSocketErrorMessage(message.message));
          socket.close();
          return;
        }

        applySnapshot(message.job);
      };

      socket.onerror = () => {
        if (!cancelled) {
          setError('Live ingestion updates are unavailable. Retrying…');
        }
      };

      socket.onclose = () => {
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        if (!cancelled && !terminalRef.current) {
          scheduleReconnect();
        }
      };
    };

    void fetchSnapshot();
    void connect();
    snapshotPollTimer = window.setInterval(() => {
      void fetchSnapshot();
    }, SNAPSHOT_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      stopSnapshotPolling();
      clearReconnectTimer();
      closeSocket();
    };
  }, [activeJobId, clearReconnectTimer, closeSocket, getAccessToken]);

  return {
    activeJobId,
    job,
    connectionState,
    error,
    trackJob,
    reset,
  };
}
